// Plain English in the add rows: "call mum friday *" becomes a task due
// Friday with high priority. Plain text is added straight away as before;
// only text that looks like it carries a date, time or detail goes to the
// assistant, and the row shows what it understood before adding it.

import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { parseQuickAdd } from './AgentContext';

const WEEKDAYS = 'mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday';
const MONTHS = 'jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december';

const TASK_SIGNALS = new RegExp([
  `\\b(today|tonight|tomorrow|tmrw|this weekend|next (week|month|${WEEKDAYS})|due|in \\d+ (days?|weeks?))\\b`,
  `\\b(${WEEKDAYS}|${MONTHS})\\b`,
  `\\b\\d{1,2}[/.-]\\d{1,2}\\b`,
  `\\b\\d{1,2}(:\\d{2})?\\s?(am|pm)\\b`,
  `\\b(urgent|asap|important|work|health|finance|money|learning|study)\\b`,
  // Priority: the words the parser understands, and * anywhere
  `\\b(low|medium|high|critical)( priority)?\\b`,
  `\\*`,
].join('|'), 'i');

// Anything a habit can store (time of day, a target, a reason), plus timing words
// the parser strips from the name ("run every day" -> "run")
const HABIT_SIGNALS = new RegExp([
  `\\b(morning|afternoon|evening|night|tonight|before bed|after (work|lunch|dinner|school)|at lunch|on waking)\\b`,
  `\\b(every ?day|everyday|daily|each day|every (morning|afternoon|evening|night)|weekdays)\\b`,
  `\\b\\d{1,2}(:\\d{2})?\\s?(am|pm)\\b`,
  `\\b(for )?\\d+\\s*days?\\b`,
  `\\b(because|so (that )?i|to (help|stop|feel|get|be|keep))\\b`,
].join('|'), 'i');

// Whether a typed line is worth sending to the assistant
export const looksDetailed = (kind, text) => (kind === 'task' ? TASK_SIGNALS : HABIT_SIGNALS).test(text);

const TIMES = { morning: 'morning', afternoon: 'afternoon', evening: 'evening', night: 'night', anytime: 'anytime' };
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const CATEGORIES = ['work', 'personal', 'health', 'finance', 'learning', 'other'];
const isDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

const formatDue = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
};

/**
 * An `interpret` prop for QuickAddRow: { shouldTry(text), run(text) ->
 * { summary, fields }, apply(fields) }. apply is read from the latest render
 * when the user confirms, so it never adds from stale lists. Undefined
 * when the assistant isn't available (not Pro), so the row behaves exactly
 * as it always has.
 * `defaults` fills what the text doesn't say (Today's row: due today).
 */
export const useQuickAddInterpreter = (kind, defaults = {}) => {
  const { addTask, addHabit } = useApp();
  const { isPro } = useSubscription();
  const defaultDue = defaults.dueDate || '';

  return useMemo(() => {
    if (!isPro) return undefined;

    if (kind === 'task') {
      return {
        shouldTry: (text) => looksDetailed('task', text),
        run: async (text) => {
          const f = await parseQuickAdd('task', text);
          const title = (f?.title || '').trim() || text;
          const dueDate = isDate(f?.due_date) ? f.due_date : defaultDue;
          const weight = PRIORITIES.includes(f?.priority) ? f.priority : (text.includes('*') ? 'high' : 'medium');
          const category = CATEGORIES.includes(f?.category) ? f.category : 'personal';
          const parts = [title];
          if (dueDate) parts.push(`due ${formatDue(dueDate)}`);
          if (weight !== 'medium') parts.push(`${weight} priority`);
          if (category !== 'personal') parts.push(category);
          return { summary: parts.join(' · '), fields: { title, description: '', dueDate, category, weight } };
        },
        apply: (fields) => addTask(fields),
      };
    }

    return {
      shouldTry: (text) => looksDetailed('habit', text),
      run: async (text) => {
        const f = await parseQuickAdd('habit', text);
        const name = (f?.name || '').trim() || text;
        const timeOfDay = TIMES[f?.time_of_day] || 'anytime';
        const goal = Number.isInteger(f?.target_days) && f.target_days > 0 && f.target_days <= 366 ? f.target_days : 30;
        const why = typeof f?.why === 'string' && f.why.trim() ? f.why.trim() : undefined;
        const parts = [name];
        if (timeOfDay !== 'anytime') parts.push(timeOfDay);
        if (goal !== 30) parts.push(`${goal} days`);
        if (why) parts.push(`why: ${why}`);
        return { summary: parts.join(' · '), fields: { name, timeOfDay, goal, why } };
      },
      apply: (fields) => addHabit(fields),
    };
  }, [kind, isPro, addTask, addHabit, defaultDue]);
};
